import {useEffect, useRef, useState} from 'react';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import Viewport from '../../helpers/Viewport';
import Activity from "../../helpers/Activity";
import CatalogSearch from '../../helpers/CatalogSearch';
import Text from '../../helpers/Text';
import searchGreyIcon from './images/search-grey.png';
import searchBlackIcon from './images/search-black.png';
import './SearchModal.css';

function SearchModal({
    client,
    catalog,
    inputBorderColor,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    cancelButtonBackgroundColor,
    cancelButtonBorderColor,
    cancelButtonLabelColor,
    onSearch,
    onClose,
    metadata
}) {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const inputRef = useRef(null);
    const smallModeTreshold = {width: 768, height: 600};
    const viewport = Viewport.dimensions;
    const minQueryLength = CatalogSearch.minQueryLength;
    const maxSuggestions = 30;

    useEffect(() => {
        setTimeout(() => {
            focusSearchInput();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (query.trim().length === 0) setSuggestions([]);
        else {
            CatalogSearch.match(client, catalog, query, maxSuggestions)
                .then((results) => {
                    if (!cancelled) setSuggestions(results);
                })
                .catch(() => {
                    if (!cancelled) setSuggestions([]);
                });
        }
        return () => {
            cancelled = true;
        };
    }, [client, catalog, query]);

    const focusSearchInput = () => {
        if (inputRef.current) inputRef.current.focus();
    };

    const inSmallMode = () => {
        return viewport.width <= smallModeTreshold.width || viewport.height <= smallModeTreshold.height;
    };

    const validQuery = (query) => {
        return query && query.length >= minQueryLength;
    };

    const handleSearch = (query) => {
        if (onSearch && validQuery(query)) {
            Activity.log(metadata, 'search-modal', 'search-button', 'search', true, {query: query})
                .then(() => {onSearch(query, undefined)});
        }
        silentClose();
    };

    const handleSuggestionClick = (value, index) => {
        if (onSearch) {
            Activity.log(metadata, 'search-modal', 'search-suggestion', 'search', true, {suggestion: value})
                .then(() => {onSearch(value, index + 1)});
        }
        silentClose();
    };

    const silentClose = () => {
        if (onClose) onClose();
    };

    const handleClose = (closeType) => {
        if (onClose) {
            Activity.log(metadata, 'search-modal', closeType, 'close', true, undefined);
            onClose();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') handleSearch(query);
    };

    const inputStyle = () => {
        const inFocus = document.activeElement === inputRef.current;
        return {
            border: `2px solid ${inFocus ? (inputBorderColor || '#005B80') : '#B2B2B2'}`
        };
    };

    const renderHighlightedName = (name) => {
        const normalizedName = Text.normalize(name);
        const tokens = Text.normalize(query).trim().split(/\s+/).filter((token) => token.length > 0);
        const highlighted = new Array(name.length).fill(false);
        tokens.forEach((token) => {
            let position = normalizedName.indexOf(token);
            while (position >= 0) {
                for (let i = position; i < position + token.length; i++) highlighted[i] = true;
                position = normalizedName.indexOf(token, position + token.length);
            }
        });
        let parts = [];
        let current = {text: '', highlighted: highlighted[0]};
        for (let i = 0; i < name.length; i++) {
            if (highlighted[i] !== current.highlighted) {
                parts.push(current);
                current = {text: '', highlighted: highlighted[i]};
            }
            current.text += name[i];
        }
        parts.push(current);
        return parts.map((part, index) => part.highlighted ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>);
    };

    const renderSuggestion = (match, index) => {
        const name = match.name || '';
        return (
            <div key={`${match.productId}-${match.variantId || ''}`} className='suggestion' onClick={() => handleSuggestionClick(name, index)}>
                <img className='suggestion-icon' src={`${searchBlackIcon}`} alt='Suggestion'/>
                <p className='suggestion-text' style={{color: '#414042'}}>{renderHighlightedName(name)}</p>
            </div>
        );
    };

    return (
        <Modal mode='center' smallMode={inSmallMode()} backgroundColor='#FFFFFF' borderRadius={{topLeft: 50, topRight: 50, bottomLeft: 50, bottomRight: 50}} padding={40} width={800} maxWidth='100%' height='auto' maxHeight='100%' showDefaultCloseButton={false} onClose={handleClose}>
            <div className='search-modal'>
                <p className='search-modal-title'>Buscar</p>
                <p className='search-modal-description'>Ingresa el criterio de búsqueda para encontrar los productos perfectos para ti</p>
                <div className='search-modal-input' style={inputStyle()}>
                    <img className='search-modal-input-icon' src={`${searchGreyIcon}`} alt='Search'/>
                    <input ref={inputRef} className='search-modal-input-field' type='search' placeholder='Buscar...' autoComplete='off' value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={handleKeyDown} onBlur={focusSearchInput}/>
                </div>
                {!validQuery(query) && <p className='search-modal-input-guide'>Ingrese 3 o más caracteres para una búsuqeda más precisa</p>}
                <div className='search-modal-suggestions'>
                    {suggestions.length > 0 && (
                        <div className='search-modal-suggestions-panel'>
                            {suggestions.map((match, index) => renderSuggestion(match, index))}
                        </div>
                    )}
                </div>
                <div className='search-modal-actions'>
                    <div className='search-modal-action'>
                        <Button label='Cerrar' labelColor={cancelButtonLabelColor} backgroundColor={cancelButtonBackgroundColor} borderColor={cancelButtonBorderColor} minWidth='auto' maxWidth='100%' boxShadow='unset' onClick={() => handleClose('custom-close-button')}/>
                    </div>
                    <div className='search-modal-action'>
                        <Button label='Buscar' labelColor={actionButtonLabelColor} backgroundColor={actionButtonBackgroundColor} borderColor={actionButtonBorderColor} minWidth='auto' maxWidth='100%' boxShadow='unset' disabled={!validQuery(query)} onClick={() => handleSearch(query)}/>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default SearchModal;